/**
 * Studio Service
 *
 * Home / navigation / pricing content is static (see `@/@studio/data/studio-content`).
 * Support tickets and route-visit logging still hit the portal API.
 */

import { Pricing } from '@/@account/types';
import { ERROR_MESSAGES, NavigationPage } from '@/@cms/types';
import { getStudioNavigationPage, getStudioPricing } from '@/@studio/data/studio-content';
import { APIEndpoints as StudioApiEndpoints } from '@/@studio/types/constants';
import { EVENTS, tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';

export { getStudioPricing } from '@/@studio/data/studio-content';

/**
 * Service class for Studio static content and portal-backed actions.
 */
export class StudioService {
    /**
     * Returns a static navigation page by slug.
     *
     * @param slug - Navigation page slug
     * @throws Error if slug is missing or unknown
     */
    async getNavigationPage(slug?: string): Promise<NavigationPage> {
        if (!slug) {
            throw new Error('Navigation slug is required');
        }

        const page = getStudioNavigationPage(slug);
        if (!page) {
            tracking.errorWithSource(
                new Error(`No navigation page found for slug: ${slug}`),
                'studio.StudioService.getNavigationPage',
            );
            throw new Error(`No navigation page found for slug: ${slug}`);
        }

        return page;
    }

    /**
     * Returns static billing pricing copy.
     */
    async getPricing(): Promise<Pricing> {
        return getStudioPricing();
    }

    async logUserRouteVisit(route: string): Promise<void> {
        try {
            await portalApi.post(StudioApiEndpoints.USER_ROUTE_VISIT, { route });
            tracking.event(EVENTS.STUDIO.USER_ROUTE_VISIT, { route });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to log user route visit: ${errorMessage}`),
                'studio.StudioService.logUserRouteVisit',
                { route },
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to log user route visit: ${errorMessage}`),
            );
        }
    }

    /**
     * Sends a support ticket to Zendesk.
     */
    async sendSupportTicket(ticketData: {
        subject: string;
        userInput: string;
        userEmail?: string;
        userName?: string;
    }): Promise<void> {
        tracking.event(EVENTS.STUDIO.SEND_SUPPORT_TICKET_ATTEMPT, {
            subject: ticketData.subject,
            userEmail: ticketData.userEmail,
        });
        try {
            await portalApi.post(StudioApiEndpoints.ZENDESK_TICKET, ticketData);
            tracking.event(EVENTS.STUDIO.SEND_SUPPORT_TICKET_SUCCESS, {
                subject: ticketData.subject,
                userEmail: ticketData.userEmail,
            });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to send support ticket: ${errorMessage}`),
                'studio.StudioService.sendSupportTicket',
                { subject: ticketData.subject },
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to send support ticket: ${errorMessage}`),
            );
        }
    }
}

export const studioService = new StudioService();

export default StudioService;
